import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  advanceAttendance,
  advancePass,
  resetQuests,
  resetTimedQuests,
  setAttendanceCheckInCount,
  setStageStars,
  setTotalStarCount,
  setUserStage,
  setUserTutorials,
} from '@/client/admin.client';
import { ALL_TUTORIAL_IDS } from '@rb/shared/tutorial/tutorial-ids';
import ValhallaProgressSection from '@/components/admin/ValhallaProgressSection';

interface Props {
  account: string;
  state: Record<string, unknown> | null;
  /** 적용 성공 후 부모가 getUserDetail 을 재조회하도록 트리거. */
  onApplied: () => void;
}

function readStage(state: Record<string, unknown> | null): number {
  const v = state?.lastClearedStageId;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function readTutorials(state: Record<string, unknown> | null): Set<string> {
  const v = state?.completedTutorials;
  return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
}

function readAttendance(
  state: Record<string, unknown> | null,
  field: 'eventAttendance' | 'attendance'
): {
  checkInCount: number;
  lastClaimDate: string;
  weekIndex: number;
} {
  const a = state?.[field] as Record<string, unknown> | undefined;
  return {
    checkInCount: typeof a?.checkInCount === 'number' ? a.checkInCount : 0,
    lastClaimDate: typeof a?.lastClaimDate === 'string' ? a.lastClaimDate : '-',
    weekIndex: typeof a?.weekIndex === 'number' ? a.weekIndex : 0,
  };
}

function readPassCount(state: Record<string, unknown> | null): number {
  const p = state?.passes as Record<string, unknown> | undefined;
  return p ? Object.keys(p).length : 0;
}

function readTotalStars(state: Record<string, unknown> | null): number {
  const v = state?.totalStarCount;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Staging·dev 전용 진행도 오버라이드 패널 — 대상 유저의 lastClearedStageId 와
 * completedTutorials 셋을 임의 값으로 설정한다. 또한 시간 조절 컨트롤로
 * 이벤트 출석(eventAttendance)·일반 출석(attendance)을 각각 앞당기고,
 * 데일리·위클리 퀘스트를 reset 하며, 패스를 진행시킨다.
 * prod verse 에서는 부모가 아예 렌더하지 않으며, 서버도 prod 를 최종 차단한다.
 *
 * 부모는 `key={account}` 로 마운트해 유저 전환 시 로컬 편집 상태가 초기화되게 한다.
 */
export function UserProgressDevPanel({ account, state, onApplied }: Props) {
  const [stageInput, setStageInput] = useState<string>(String(readStage(state)));
  const [stageSaving, setStageSaving] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);

  const [tutorials, setTutorials] = useState<Set<string>>(() => readTutorials(state));
  const [tutorialsSaving, setTutorialsSaving] = useState(false);
  const [tutorialsError, setTutorialsError] = useState<string | null>(null);

  const [timeError, setTimeError] = useState<string | null>(null);
  const [timeBusy, setTimeBusy] = useState(false);
  const eventAtt = readAttendance(state, 'eventAttendance');
  const regularAtt = readAttendance(state, 'attendance');
  const passCount = readPassCount(state);

  const totalStars = readTotalStars(state);
  const [totalStarInput, setTotalStarInput] = useState<string>(String(totalStars));
  const [stageStarId, setStageStarId] = useState<string>('1');
  const [stageStarLevel, setStageStarLevel] = useState<string>('3');
  const [starBusy, setStarBusy] = useState(false);
  const [starError, setStarError] = useState<string | null>(null);

  const runTimeAction = async (fn: () => Promise<unknown>) => {
    setTimeError(null);
    setTimeBusy(true);
    try {
      await fn();
      onApplied();
    } catch (err) {
      setTimeError(err instanceof Error ? err.message : String(err));
    } finally {
      setTimeBusy(false);
    }
  };

  // 적용 성공 → 부모가 같은 account 를 getUserDetail 로 재조회 → state prop 갱신.
  // key={account} 리마운트는 유저 전환 때만 일어나므로, 동일 유저 재조회 시
  // 폼을 서버 최신값으로 재동기화한다(서버가 입력을 변형한 경우 시각적 불일치 방지).
  // state 참조는 search/refetch 시점에만 바뀌어 편집 중 클로버 위험은 없다.
  useEffect(() => {
    setStageInput(String(readStage(state)));
    setTutorials(readTutorials(state));
    setTotalStarInput(String(readTotalStars(state)));
    // stageStarId/stageStarLevel 은 의도적으로 재동기화하지 않는다 — 단일 state 필드의
    // 미러가 아니라 "어떤 스테이지를 몇 별로" 지정하는 입력 폼이라 반영할 현재값이 없다.
  }, [state]);

  const toggleTutorial = (id: string) =>
    setTutorials((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSetStage = async () => {
    setStageError(null);
    const n = Number(stageInput);
    if (!Number.isInteger(n) || n < 0) {
      setStageError('stage must be a non-negative integer');
      return;
    }
    setStageSaving(true);
    try {
      await setUserStage(account, n);
      onApplied();
    } catch (err) {
      setStageError(err instanceof Error ? err.message : String(err));
    } finally {
      setStageSaving(false);
    }
  };

  const handleSetTutorials = async () => {
    setTutorialsError(null);
    setTutorialsSaving(true);
    try {
      // ALL_TUTORIAL_IDS 순서를 유지해 결정적 배열을 보낸다.
      const ids = ALL_TUTORIAL_IDS.filter((id) => tutorials.has(id));
      await setUserTutorials(account, ids);
      onApplied();
    } catch (err) {
      setTutorialsError(err instanceof Error ? err.message : String(err));
    } finally {
      setTutorialsSaving(false);
    }
  };

  const handleSetTotalStars = async () => {
    setStarError(null);
    const n = Number(totalStarInput);
    if (!Number.isInteger(n) || n < 0) {
      setStarError('별 개수는 0 이상의 정수여야 합니다');
      return;
    }
    setStarBusy(true);
    try {
      await setTotalStarCount(account, n);
      onApplied();
    } catch (err) {
      setStarError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarBusy(false);
    }
  };

  const handleSetStageStars = async () => {
    setStarError(null);
    const id = Number(stageStarId);
    const level = Number(stageStarLevel);
    if (!Number.isInteger(id) || id < 1) {
      setStarError('stageId 는 1 이상의 정수여야 합니다');
      return;
    }
    setStarBusy(true);
    try {
      await setStageStars(account, id, level);
      onApplied();
    } catch (err) {
      setStarError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Progress override
        </h4>
        <Badge variant="outline" className="border-amber-500/60 text-amber-600">
          Dev/Staging only
        </Badge>
      </div>

      {/* Stage */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="set-stage" className="text-xs text-muted-foreground">
              lastClearedStageId
            </Label>
            <Input
              id="set-stage"
              type="number"
              min={0}
              value={stageInput}
              onChange={(e) => setStageInput(e.target.value)}
              disabled={stageSaving}
              className="w-32"
            />
          </div>
          <Button type="button" size="sm" onClick={handleSetStage} disabled={stageSaving}>
            {stageSaving ? '…' : 'Set stage'}
          </Button>
          <span className="text-xs text-muted-foreground">0 = reset (no stages cleared)</span>
        </div>
        {stageError && <p className="text-xs text-destructive">{stageError}</p>}
      </div>

      <Separator />

      {/* Tutorials */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-muted-foreground">completedTutorials</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={tutorialsSaving}
              onClick={() => setTutorials(new Set(ALL_TUTORIAL_IDS))}
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={tutorialsSaving}
              onClick={() => setTutorials(new Set())}
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {ALL_TUTORIAL_IDS.map((id) => {
            const inputId = `tut-${id}`;
            return (
              <label
                key={id}
                htmlFor={inputId}
                className="flex cursor-pointer items-center gap-1.5 text-xs"
              >
                <Checkbox
                  id={inputId}
                  checked={tutorials.has(id)}
                  onCheckedChange={() => toggleTutorial(id)}
                  disabled={tutorialsSaving}
                />
                <span className="font-mono">{id}</span>
              </label>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={handleSetTutorials} disabled={tutorialsSaving}>
            {tutorialsSaving ? '…' : 'Set tutorials'}
          </Button>
          <span className="text-xs text-muted-foreground">empty = reset</span>
        </div>
        {tutorialsError && <p className="text-xs text-destructive">{tutorialsError}</p>}
      </div>

      <Separator />

      {/* Star (별) */}
      <div className="flex flex-col gap-3">
        <Label className="text-xs text-muted-foreground">별 (Star)</Label>

        {/* 누적 별 */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="set-total-stars" className="text-xs text-muted-foreground">
              누적 별 (totalStarCount) · 현재 {totalStars}
            </Label>
            <Input
              id="set-total-stars"
              type="number"
              min={0}
              value={totalStarInput}
              onChange={(e) => setTotalStarInput(e.target.value)}
              disabled={starBusy}
              className="w-32"
            />
          </div>
          <Button type="button" size="sm" onClick={handleSetTotalStars} disabled={starBusy}>
            {starBusy ? '…' : '누적 별 설정'}
          </Button>
          <span className="text-xs text-muted-foreground">
            별모으기 이벤트 / TOTAL_STAR_COUNT 퀘스트용
          </span>
        </div>

        {/* 스테이지별 별점 */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="stage-star-id" className="text-xs text-muted-foreground">
              stageId
            </Label>
            <Input
              id="stage-star-id"
              type="number"
              min={1}
              value={stageStarId}
              onChange={(e) => setStageStarId(e.target.value)}
              disabled={starBusy}
              className="w-24"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="stage-star-level" className="text-xs text-muted-foreground">
              별점
            </Label>
            <select
              id="stage-star-level"
              value={stageStarLevel}
              onChange={(e) => setStageStarLevel(e.target.value)}
              disabled={starBusy}
              className="h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="0">0 (리셋)</option>
              <option value="1">1★</option>
              <option value="2">2★</option>
              <option value="3">3★</option>
            </select>
          </div>
          <Button type="button" size="sm" onClick={handleSetStageStars} disabled={starBusy}>
            {starBusy ? '…' : '별점 설정'}
          </Button>
          <span className="text-xs text-muted-foreground">누적 별도 차이만큼 자동 보정</span>
        </div>

        {starError && <p className="text-xs text-destructive">{starError}</p>}
      </div>

      <Separator />

      {/* Valhalla (발할라) — 활성 시즌 기준 */}
      <ValhallaProgressSection account={account} state={state} onApplied={onApplied} />

      <Separator />

      {/* Progression time control */}
      <div className="flex flex-col gap-3">
        <Label className="text-xs text-muted-foreground">Progression time (시간 조절)</Label>

        {/* 이벤트 출석 */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono text-muted-foreground">
            이벤트 출석 · day {eventAtt.checkInCount} · last {eventAtt.lastClaimDate} · week{' '}
            {eventAtt.weekIndex}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 1, 'event'))}
            >
              다음날
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 3, 'event'))}
            >
              +3일
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 7, 'event'))}
            >
              +7일
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => setAttendanceCheckInCount(account, 6, 'event'))}
            >
              7일차로 점프
            </Button>
          </div>
        </div>

        {/* 일반 출석 */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono text-muted-foreground">
            일반 출석 · day {regularAtt.checkInCount} · last {regularAtt.lastClaimDate} · week{' '}
            {regularAtt.weekIndex}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 1, 'regular'))}
            >
              다음날
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 3, 'regular'))}
            >
              +3일
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => advanceAttendance(account, 7, 'regular'))}
            >
              +7일
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => setAttendanceCheckInCount(account, 6, 'regular'))}
            >
              7일차로 점프
            </Button>
          </div>
        </div>

        {/* 퀘스트 */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono text-muted-foreground">퀘스트 reset</span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => resetQuests(account, 'DAILY'))}
            >
              데일리 reset
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => resetQuests(account, 'WEEKLY'))}
            >
              위클리 reset
            </Button>
          </div>
        </div>

        {/* 타임드 퀘스트 — 벽시계 기반이라 QA 가 n분을 기다릴 수 없어 강제 조작으로만 재현 가능 */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono text-muted-foreground">
            타임드 퀘스트 (벽시계 기반 — 만료/임박 카드 강제 재현)
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => resetTimedQuests(account, 'clear'))}
            >
              비우기
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => resetTimedQuests(account, 'expire'))}
            >
              즉시 만료
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy}
              onClick={() => runTimeAction(() => resetTimedQuests(account, 'imminent'))}
            >
              30초 후 만료
            </Button>
          </div>
        </div>

        {/* 패스 */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono text-muted-foreground">
            패스 · {passCount}개 보유 (해금일+만료일+퍽 동시 당김)
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy || passCount === 0}
              onClick={() => runTimeAction(() => advancePass(account, 1))}
            >
              다음날
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy || passCount === 0}
              onClick={() => runTimeAction(() => advancePass(account, 3))}
            >
              +3일
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy || passCount === 0}
              onClick={() => runTimeAction(() => advancePass(account, 7))}
            >
              +7일
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={timeBusy || passCount === 0}
              onClick={() => runTimeAction(() => advancePass(account, 30))}
            >
              +30일(만료)
            </Button>
          </div>
        </div>

        {timeError && <p className="text-xs text-destructive">{timeError}</p>}
      </div>
    </div>
  );
}

export default UserProgressDevPanel;
